<template>
  <div class="system-log-container">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <o-bread-crumb url="/extranet/feature-flags">
            <template v-slot:pageName>Feature Flags</template>
            <template v-slot:pageDetail>Audit Log</template>
            <template v-slot:pageDescription><br/></template>
          </o-bread-crumb>
          <h1 class="page-title mt-2">Feature Flag Audit Logs</h1>
          <p class="page-subtitle">Historical configuration changes for Corporate {{ corporateName }}</p>
        </div>
        <div class="header-right">
          <button class="btn btn-refresh-header" @click="loadData" :disabled="page.isLoading">
            <i class="fas fa-sync-alt me-2" :class="{ 'fa-spin': page.isLoading }"></i>
            Refresh Logs
          </button>
        </div>
      </div>
    </div>

    <!-- Main Card Container -->
    <div class="management-card">
      <!-- Filter Bar -->
      <div class="filter-bar">
        <div class="filter-left">
          <!-- Search Control -->
          <div class="search-box-custom">
            <div class="search-type-tabs">
              <button 
                v-for="option in searchOptions" 
                :key="option.value"
                class="tab-btn"
                :class="{ active: searchBy.selected === option.value }"
                @click="selectSearchType(option.value)"
              >
                {{ option.text }}
              </button>
            </div>
            <div class="search-input-wrapper">
              <input 
                type="text" 
                class="form-control-search" 
                v-model="searchBy.criteria" 
                placeholder="Search..." 
                @keyup.enter="applyFilters" 
              />
              <button class="btn-search" @click="applyFilters">
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>
        </div>
        
        <div class="filter-right">
          <div class="stats-summary">
            <span class="stats-label">Total Results:</span>
            <span class="stats-value">{{ meta.total || 0 }}</span>
          </div>
          <button class="btn-reset-primary" @click="clearFilters" title="Reset All Filters">
            <i class="fas fa-undo-alt"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="card-content">
        <!-- Loading State -->
        <div v-if="page.isLoading && !page.output" class="loading-state">
          <div class="loader-box">
            <div class="spinner-border text-danger" role="status"></div>
            <p>Fetching feature flag logs...</p>
          </div>
        </div>

        <!-- Table Content -->
        <div v-else-if="page.output" class="table-outer">
          <div class="table-responsive">
            <table class="modern-list-table">
              <thead>
                <tr>
                  <th width="60" class="col-no">No</th>
                  <th width="180">Timestamp</th>
                  <th width="200">User Identity</th>
                  <th width="180">Feature Key</th>
                  <th width="200">Flag Change</th>
                  <th class="text-end pe-4" width="100">Metadata</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="auditLogs.length === 0">
                  <tr>
                    <td colspan="6">
                      <div class="empty-list">
                        <div class="empty-art">
                          <i class="fas fa-search"></i>
                        </div>
                        <h3>No Logs Found</h3>
                        <p>Try adjusting your search filters to find what you're looking for.</p>
                        <button class="btn-clear-empty mt-3" @click="clearFilters">
                          <i class="fas fa-eraser me-2"></i>
                          Clear All Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>

                <template v-else v-for="(log, index) in auditLogs" :key="log.id || index">
                  <tr 
                    :class="{'expanded': expandedRows.includes(index)}"
                    @click="toggleRow(index)"
                    class="clickable-row"
                  >
                    <td class="col-no" @click.stop>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                    <td>
                      <div class="timestamp-box">
                        <span class="date">{{ formatDate(log.changed_at) }}</span>
                        <span class="time">{{ formatTime(log.changed_at) }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="user-info-box">
                        <div class="user-avatar">
                          {{ (log.changed_by_name || 'U').charAt(0).toUpperCase() }}
                        </div>
                        <div class="user-details">
                          <span class="user-name">{{ log.changed_by_name || 'Unknown User' }}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="component-pill">
                        {{ log.feature === "point_control" ? "Point Control Mode" : log.feature }}
                      </span>
                    </td>
                    <td>
                       <div class="flag-change-box">
                         <span class="flag-badge" :class="getBadgeClass(log.old_flag)">{{ log.old_flag || 'NONE' }}</span>
                         <i class="fas fa-arrow-right text-muted mx-2" style="font-size: 10px;"></i>
                         <span class="flag-badge" :class="getBadgeClass(log.new_flag)">{{ log.new_flag || 'NONE' }}</span>
                       </div>
                    </td>
                    <td class="text-end pe-4">
                      <button 
                        class="action-btn" 
                        :class="{ active: expandedRows.includes(index) }"
                        @click.stop="toggleRow(index)"
                        title="Toggle Metadata Details"
                      >
                        <i class="fas" :class="expandedRows.includes(index) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                    </td>
                  </tr>

                  <!-- Expanded Detail Row -->
                  <tr v-if="expandedRows.includes(index)" class="detail-row">
                    <td colspan="6" class="detail-container">
                      <div class="detail-content">
                        <div class="row g-4">
                          <!-- Old Metadata -->
                          <div class="col-md-6">
                            <div class="state-card" :class="{ 'no-data': !log.old_metadata }">
                              <div class="state-header before">
                                <i class="fas fa-history me-2"></i>
                                Old Metadata
                              </div>
                              <div v-if="log.old_metadata" class="state-body custom-json-pretty">
                                <vue-json-pretty 
                                  :data="parseJsonSafely(log.old_metadata)" 
                                  :deep="1" 
                                  :showDoubleQuotes="true"
                                  :showLength="true"
                                  :showLine="true"
                                  :theme="'dark'"
                                />
                              </div>
                              <div v-else class="state-body empty">
                                <div class="empty-placeholder">
                                  <span>No Previous Metadata</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <!-- New Metadata -->
                          <div class="col-md-6">
                            <div class="state-card" :class="{ 'no-data': !log.new_metadata }">
                              <div class="state-header after">
                                <i class="fas fa-check-circle me-2"></i>
                                New Metadata
                              </div>
                              <div v-if="log.new_metadata" class="state-body custom-json-pretty">
                                <vue-json-pretty 
                                  :data="parseJsonSafely(log.new_metadata)" 
                                  :deep="1" 
                                  :showDoubleQuotes="true"
                                  :showLength="true"
                                  :showLine="true"
                                  :theme="'dark'"
                                />
                              </div>
                              <div v-else class="state-body empty">
                                <div class="empty-placeholder">
                                  <span>No New Metadata</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Footer / Pagination -->
      <div class="extranet-pagination-footer" v-if="page.output && auditLogs.length > 0">
        <o-pagination 
          :page="meta.page"  
          :total="meta.total_pages" 
          @callback="navPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { OBreadCrumb, OPagination } from '@/components/OmnihControl'
import { reactive, ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import VueJsonPretty from "vue-json-pretty"
import "vue-json-pretty/lib/styles.css"
import api from '@/helpers/api'

export default {
  name: 'ExtranetFeatureFlagAudit',
  
  components: {
    OBreadCrumb,
    OPagination,
    VueJsonPretty
  },

  setup() {
    const route = useRoute()
    const store = useStore()
    const expandedRows = ref([])
    const corporateId = ref(route.params.corporateId || '')
    const activeCorporateName = computed(() => store.state.extranet?.selectedTenant?.name || 'No Corporate Selected')
    
    // In extranet context, we generally follow the nav bar selected tenant
    // If empty param, fetch current active from store
    if (!corporateId.value && store.state.extranet?.selectedTenant?.id) {
        corporateId.value = store.state.extranet.selectedTenant.id
    }

    const page = reactive({
      isLoading: false,
      output: false
    })

    const auditLogs = ref([])

    const meta = reactive({
      page: 1,
      limit: 15,
      total: 0,
      total_pages: 1
    })

    const searchOptions = [
        { value: 'feature', text: 'Feature Key' },
        { value: 'changed_by_name', text: 'Changed By' }
    ]

    const searchBy = reactive({
        selected: 'feature',
        criteria: ''
    })

    const loadData = async () => {
      if (!corporateId.value) return

      page.isLoading = true
      expandedRows.value = []

      try {
        const filterParams = {}
        if (searchBy.criteria) {
          filterParams[searchBy.selected] = searchBy.criteria
        }

        const params = {
          meta: {
            page: meta.page,
            limit: meta.limit
          },
          filter: filterParams
        }

        const response = await api.request('get', `/api/extranet/feature-flags/${corporateId.value}/audit-logs`, params)

        if (response.data && response.data.data) {
          const resData = response.data.data
          auditLogs.value = resData.data_res || []
          
          if (resData.meta) {
            meta.page = parseInt(resData.meta.page) || parseInt(resData.meta.current_page) || 1
            meta.limit = parseInt(resData.meta.limit) || parseInt(resData.meta.per_page) || 15
            
            // Backend's "total" is actually the total number of pages due to `ceil(page_count / limit)`
            meta.total_pages = parseInt(resData.meta.total) || 1
            // Use the newly added "total_data" for exact item count, fallback to zero
            meta.total = parseInt(resData.meta.total_data) || 0
          }
        }
        page.output = true
      } catch (error) {
        console.error('Error loading audit logs:', error)
      } finally {
        page.isLoading = false
      }
    }

    function navPage(pageNumber) {
      if (pageNumber === meta.page) return
      meta.page = pageNumber
      loadData()
    }

    function applyFilters() {
      meta.page = 1
      loadData()
    }

    function clearFilters() {
      searchBy.criteria = ''
      meta.page = 1
      loadData()
    }
    
    function selectSearchType(type) {
      searchBy.selected = type
      if (searchBy.criteria) applyFilters()
    }

    function toggleRow(index) {
      const pos = expandedRows.value.indexOf(index)
      if (pos > -1) {
        expandedRows.value.splice(pos, 1)
      } else {
        expandedRows.value.push(index)
      }
    }

    function formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    }

    function formatTime(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      })
    }

    function parseJsonSafely(json) {
      try {
        if (typeof json === 'string') {
          return JSON.parse(json)
        }
        return json
      } catch (e) {
        return { error: 'Invalid JSON format', raw: json }
      }
    }

    function getBadgeClass(flag) {
      switch (flag) {
        case 'NONACTIVE':
          return 'flag--danger'
        case 'CENTRALIZE':
          return 'flag--primary'
        case 'DECENTRALIZE':
          return 'flag--success'
        default:
          return 'flag--secondary'
      }
    }

    onMounted(() => {
        if (route.query && route.query.feature) {
            searchBy.selected = 'feature'
            searchBy.criteria = route.query.feature
        }
        loadData()
    })

    return {
      page,
      meta,
      auditLogs,
      expandedRows,
      searchBy,
      searchOptions,
      corporateName: activeCorporateName,
      loadData,
      navPage,
      applyFilters,
      clearFilters,
      selectSearchType,
      toggleRow,
      formatDate,
      formatTime,
      parseJsonSafely,
      getBadgeClass
    }
  }
}
</script>

<style scoped>
.system-log-container {
    max-width: 1400px;
    margin: 0 auto;
}

/* Page Header */
.page-header {
    margin-bottom: 32px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.page-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
}

/* Management Card */
.management-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
}

/* Filter Bar */
.filter-bar {
    padding: 24px 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    gap: 24px;
}

.filter-left {
    display: flex;
    align-items: flex-end;
    gap: 32px;
    flex: 1;
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 400px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: var(--extranet-rounded);
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    border-radius: var(--extranet-rounded);
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--extranet-primary);
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--extranet-primary);
    background: white;
    box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.05);
}

.form-control-search {
    border: none;
    background: transparent;
    padding: 8px 12px;
    font-size: 14px;
    color: #1e293b;
    width: 100%;
    outline: none;
}

.btn-search {
    background: var(--extranet-primary);
    border: 1px solid var(--extranet-primary);
    width: 36px;
    height: 36px;
    border-radius: var(--extranet-rounded);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

.btn-reset-primary, .btn-refresh-header, .btn-clear-empty {
    height: 38px;
    border: none;
    background: var(--extranet-primary);
    border-radius: var(--extranet-rounded);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: 600;
}

.btn-reset-primary:hover, .btn-refresh-header:hover, .btn-clear-empty:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

.btn-reset-primary {
    width: 38px;
}

.btn-refresh-header {
    padding: 0 20px;
    font-size: 14px;
}

.btn-clear-empty {
    padding: 0 24px;
    margin: 0 auto;
}

.filter-right {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stats-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #f8fafc;
    border-radius: var(--extranet-rounded);
    font-size: 13px;
    border: 1px solid var(--extranet-accent);
}

.stats-label { color: #64748b; font-weight: 500; }
.stats-value { color: #0f172a; font-weight: 700; }

/* Table Area */
.modern-list-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-list-table thead th {
    padding: 16px 24px;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid var(--extranet-accent);
}

.modern-list-table tbody td {
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    background: white;
    transition: background 0.2s;
}

.clickable-row {
    cursor: pointer;
}

.clickable-row:hover td {
    background: #f8fafc;
}

.clickable-row.expanded td {
    background: #f8fafc;
    border-bottom-color: transparent;
}

/* User Info Box */
.user-info-box {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #e2e8f0;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
}

.user-details {
    display: flex;
    flex-direction: column;
}

.user-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 14px;
}

/* Component Pill */
.component-pill {
    padding: 6px 12px;
    background: #f1f5f9;
    border-radius: 6px;
    font-size: 12px;
    font-family: 'JetBrains Mono', monospace;
    color: #475569;
    font-weight: 600;
    border: 1px solid #e2e8f0;
}

/* Actions */
.action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    cursor: pointer;
}

.action-btn:hover {
    color: var(--extranet-primary);
    border-color: var(--extranet-primary);
    background: #fff1f1;
}

.action-btn.active {
    background: var(--extranet-primary);
    border-color: var(--extranet-primary);
    color: white;
}

/* Timestamp Box */
.timestamp-box {
    display: flex;
    flex-direction: column;
}

.timestamp-box .date {
    font-weight: 600;
    color: #1e293b;
    font-size: 13px;
}

.timestamp-box .time {
    font-size: 12px;
    color: #64748b;
}

/* Detail Row */
.detail-row td {
    padding: 0 24px 24px !important;
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
}

.detail-content {
    background: white;
    border: 1px solid var(--extranet-accent);
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.state-card {
    border: 1px solid var(--extranet-accent);
    border-radius: 10px;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.state-card.no-data {
    border-style: dashed;
    border-color: #cbd5e1;
    background: #f8fafc;
}

.state-header {
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--extranet-accent);
}

.state-header.before {
    background: #f8fafc;
    color: #64748b;
}

.state-header.after {
    background: #f0fdf4;
    color: #166534;
    border-bottom-color: #bbf7d0;
}

.state-card.no-data .state-header {
    border-bottom: none;
}

.state-body {
    padding: 16px;
    flex: 1;
    overflow-x: auto;
}

.state-body.empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 150px;
}

.empty-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #94a3b8;
    font-weight: 500;
    font-size: 14px;
}

/* JSON Pretty Customization inside dark container */
.custom-json-pretty {
    background: #0f172a;
    color: #e2e8f0;
}

.custom-json-pretty :deep(.vjs-value__string) {
    color: #a5b4fc;
}

.custom-json-pretty :deep(.vjs-key) {
    color: #93c5fd;
}

/* Flag Change Box */
.flag-change-box {
    display: flex;
    align-items: center;
}

.flag-badge {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 6px;
    font-weight: 700;
}

.flag--danger { background: #fee2e2; color: #991b1b; }
.flag--primary { background: #dbeafe; color: #1e40af; }
.flag--success { background: #dcfce7; color: #166534; }
.flag--secondary { background: #f1f5f9; color: #475569; }

/* Loading State */
.loading-state {
    padding: 80px 0;
    display: flex;
    justify-content: center;
}

.loader-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #64748b;
    font-weight: 500;
}

/* Empty State */
.empty-list {
    padding: 60px 0;
    text-align: center;
}

.empty-art {
    font-size: 48px;
    color: #e2e8f0;
    margin-bottom: 24px;
}

.empty-list h3 {
    color: #1e293b;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
}

.empty-list p {
    color: #64748b;
    margin-bottom: 0;
}

.extranet-pagination-footer {
    padding: 24px 32px 32px;
    background: white;
    border-top: 1px solid #f1f5f9;
}
</style>
